import { Formik } from 'formik';
import { ReactNode, useState } from 'react';
import { updateWatchConfig } from './service';
import { Button, Form, Radio, Select, message } from 'antd';
import { Container } from './LimitForm';
import useNavigateWithQuery from 'hooks/useNavigateWithQuery';
import TransComponent from 'components/TransComponent';
import { T, useTranslate } from '@tolgee/react';

type FV = NightWatch.WatchConfig['watchLimit'];

export interface ReceptionFormProps {
  editable?: boolean;
  config: NightWatch.WatchConfig;
  onUpdated: () => Promise<any>;
}

function ReceptionForm(props: ReceptionFormProps) {
  const { editable = true, config, onUpdated } = props;

  const { t } = useTranslate();

  const nav = useNavigateWithQuery();

  const [editing, setEditing] = useState(false);

  const disabled = !editable || !editing;

  return (
    <Formik<FV>
      enableReinitialize
      initialValues={config.watchLimit}
      onSubmit={(watchLimit, helpers) => {
        return updateWatchConfig(config.id, { watchLimit })
          .then(() => onUpdated())
          .then(() => {
            setEditing(false);

            helpers.resetForm();

            message.success(t('operation.saveSucceed'));
          })
          .catch(() => {
            message.error(t('operation.saveFailed'));
          });
      }}
    >
      {(props) => {
        const { values, setValues, isSubmitting, resetForm, handleSubmit } = props;

        const receptionRender = (name: keyof General.BuyerReceptionConfig, title: ReactNode) => {
          return (
            <div>
              <h5 style={{ marginBottom: '6px', fontSize: '14px' }}>{title}</h5>
              <ReceptionConfig
                disabled={disabled}
                value={values[name]}
                onChange={(value) =>
                  setValues({
                    ...values,
                    [name]: value,
                  })
                }
              />
            </div>
          );
        };

        return (
          <Container>
            <header>
              <h4>{t('reception_setting')}</h4>
              {editing ? (
                <div>
                  <Button
                    type="link"
                    onClick={() => {
                      resetForm();
                      setEditing(false);
                    }}
                  >
                    {t('translation.取消')}
                  </Button>
                  <Button type="link" loading={isSubmitting} onClick={() => handleSubmit()}>
                    {t('marketing.保存')}
                  </Button>
                </div>
              ) : (
                <Button disabled={!editable} type="link" onClick={() => setEditing(true)}>
                  {t('marketing.编辑')}
                </Button>
              )}
            </header>
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px',
                  padding: '4px 0 4px 12px',
                  borderRadius: '2px',
                  background: 'var(--fett-bg-info-tip)',
                }}
              >
                {t('buyer_type_tips')}
                <Button
                  disabled={!editable}
                  type="link"
                  onClick={() => nav('/aide/qa/groupSetting')}
                >
                  {t('remind.去配置x', { x: t('criteria_for_post_sale') })}
                </Button>
              </div>
              {receptionRender('preSaleBuyer', t('aide.售前买家'))}
              {receptionRender('inSaleBuyer', t('aide.售中买家'))}
              {receptionRender('afterSaleBuyer', t('aide.售后买家'))}
            </div>
          </Container>
        );
      }}
    </Formik>
  );
}

const hourOptions = new Array(144).fill('').map((_, index) => {
  const value = index + 1;

  return { value, label: value };
});

export function ReceptionConfig({
  disabled = false,
  value,
  onChange,
}: {
  disabled?: boolean;
  value: General.SingleReceptionConfig;
  onChange: (value: General.SingleReceptionConfig) => void;
}) {
  const { t } = useTranslate();

  return (
    <Form.Item label={t('reception_config_label')}>
      <div style={{ padding: '6px 0' }}>
        <Radio
          disabled={disabled}
          checked={value.sessionState === 1}
          onChange={({ target: { checked } }) =>
            checked &&
            onChange({
              ...value,
              sessionState: 1,
            })
          }
        >
          {t('inreception')}
        </Radio>
        <Radio
          disabled={disabled}
          checked={value.sessionState === 2}
          onChange={({ target: { checked } }) =>
            checked &&
            onChange({
              ...value,
              sessionState: 2,
            })
          }
        >
          {t('reception_completed')}
        </Radio>
      </div>
      {value.sessionState === 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: '6px 12px',
            background: 'var(--fett-bg-subtle)',
            borderRadius: '2px',
          }}
        >
          <T
            keyName="inreception_config"
            params={{
              x: (
                <TransComponent
                  content={
                    <Select
                      showSearch
                      disabled={disabled}
                      allowClear={false}
                      dropdownMatchSelectWidth={false}
                      value={value.onHoldHour}
                      options={hourOptions}
                      onChange={(onHoldHour) => onChange({ ...value, onHoldHour })}
                      style={{ width: '70px', margin: '0 6px' }}
                    />
                  }
                />
              ),
            }}
          />
        </div>
      )}
    </Form.Item>
  );
}

export default ReceptionForm;
